import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import apiService from '../../services/api'
import {
  HiOutlineUserPlus,
  HiOutlineXMark,
  HiOutlineChevronDown,
} from 'react-icons/hi2'
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  IconButton,
} from '../../components/ui'

const ROLE_OPTIONS = [
  { value: 'admin',   label: 'Admin',   desc: 'Full access',      tone: 'danger' },
  { value: 'diamond', label: 'Diamond', desc: 'High access',      tone: 'info' },
  { value: 'gold',    label: 'Gold',    desc: 'Moderate access',  tone: 'warning' },
  { value: 'silver',  label: 'Silver',  desc: 'Basic access',     tone: 'neutral' },
]

export default function ConnectionRequests() {
  const { t } = useTranslation()
  const { canManageConnections } = useAuth()
  const { getEntity } = useWorkspace()
  const connectionTerm = getEntity('connection')
  const employeeTerm = getEntity('employee')

  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [processingId, setProcessingId] = useState(null)
  const [expandedRequests, setExpandedRequests] = useState(new Set())

  useEffect(() => {
    if (!canManageConnections) return
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const response = await apiService.get('/connection-requests/received/')
        if (cancelled) return
        if (response.success) {
          const pending = (response.data.data?.results || []).filter(r => r.status === 'pending')
          setRequests(pending)
        }
      } catch (err) {
        // swallow — empty list will render
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [canManageConnections])

  const handleResponse = async (requestId, status, role = null) => {
    try {
      setProcessingId(requestId)
      const payload = { id: requestId, status }
      if (status === 'accepted' && role) payload.assigned_role = role
      const response = await apiService.post('/respond-connection-request/', payload)
      if (response.success) {
        setRequests(prev => prev.filter(req => req.id !== requestId))
      } else {
        alert(response.error?.message || 'Failed to respond to request')
      }
    } catch {
      alert('Failed to respond to request')
    } finally {
      setProcessingId(null)
    }
  }

  const toggleExpanded = (requestId) => {
    setExpandedRequests(prev => {
      const next = new Set(prev)
      if (next.has(requestId)) next.delete(requestId)
      else next.add(requestId)
      return next
    })
  }

  if (!canManageConnections) return null

  if (loading) {
    return (
      <Card padding="md">
        <Card.Header
          title={
            <span className="inline-flex items-center gap-2">
              <HiOutlineUserPlus className="w-5 h-5 text-ink-muted" />
              {t('dashboard.connections.title', '{{entity}} requests', { entity: connectionTerm.singular })}
            </span>
          }
        />
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      </Card>
    )
  }

  return (
    <Card padding="md">
      <Card.Header
        title={
          <span className="inline-flex items-center gap-2">
            <HiOutlineUserPlus className="w-5 h-5 text-ink-muted" />
            {t('dashboard.connections.title', '{{entity}} requests', { entity: connectionTerm.singular })}
            {requests.length > 0 && (
              <Badge tone="warning" size="sm">{requests.length}</Badge>
            )}
          </span>
        }
      />

      {requests.length === 0 ? (
        <EmptyState
          icon={HiOutlineUserPlus}
          title={t('dashboard.connections.emptyTitle', 'No pending requests')}
          description={t('dashboard.connections.emptyDescription', 'New {{entity}} requests will appear here', { entity: connectionTerm.singular.toLowerCase() })}
          compact
        />
      ) : (
        <ul className="space-y-3">
          {requests.map((request) => {
            const isExpanded = expandedRequests.has(request.id)
            const isProcessing = processingId === request.id
            return (
              <li key={request.id} className="border border-border rounded-xl p-3 bg-muted/40">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar name={request.sender_name} size="sm" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink-primary truncate">
                        {request.sender_name}
                      </p>
                      <p className="text-xs text-ink-muted">
                        {t('dashboard.connections.wantsToConnect', 'Wants to connect')} • {new Date(request.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <IconButton
                    icon={HiOutlineChevronDown}
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    variant="ghost"
                    size="sm"
                    disabled={isProcessing}
                    onClick={() => toggleExpanded(request.id)}
                    className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  />
                </div>

                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-border-subtle">
                    <p className="text-xs text-ink-secondary mb-3">
                      {t('dashboard.connections.chooseRole', 'Choose a role for this {{entity}}:', {
                        entity: employeeTerm.singular.toLowerCase(),
                      })}
                    </p>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {ROLE_OPTIONS.map((role) => (
                        <button
                          key={role.value}
                          type="button"
                          onClick={() => handleResponse(request.id, 'accepted', role.value)}
                          disabled={isProcessing}
                          className="p-2.5 text-start rounded-lg border border-border bg-surface hover:border-brand/40 hover:bg-brand-soft/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Badge tone={role.tone} size="sm" className="mb-1">{role.label}</Badge>
                          <p className="text-[11px] text-ink-muted">{role.desc}</p>
                        </button>
                      ))}
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      leadingIcon={HiOutlineXMark}
                      disabled={isProcessing}
                      onClick={() => handleResponse(request.id, 'rejected')}
                    >
                      {isProcessing ? t('common.loading') : t('common.cancel')}
                    </Button>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}
